import { useState } from 'react';
import { Check, ChevronsUpDown, User } from 'lucide-react';

import { Label } from '@/components/atoms/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import type { AdditionalContact } from '@/types/contact';

const SUB_CONTACT_NONE = '__primary__';

interface ContactPersonCardProps {
  selectedCustomerName: string;
  subContacts: AdditionalContact[];
  value: string;
  onChange: (v: string) => void;
}

function ContactPersonCard({
  selectedCustomerName,
  subContacts,
  value,
  onChange,
}: ContactPersonCardProps) {
  const [open, setOpen] = useState(false);
  const isPrimary = value === SUB_CONTACT_NONE;
  const selected = isPrimary ? null : subContacts.find((c) => c.id === value);

  const allOptions = [
    { id: SUB_CONTACT_NONE, name: selectedCustomerName, title: 'Primary contact' },
    ...subContacts.map((c) => ({ id: c.id, name: c.name, title: c.title ?? '' })),
  ];

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">Contact person</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="relative flex w-full cursor-pointer items-center gap-3 rounded-lg border border-border bg-muted/40 p-3 text-left text-sm transition-colors hover:bg-muted/70"
          >
            <User className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">
                {isPrimary
                  ? selectedCustomerName
                  : `${selected?.name ?? selectedCustomerName}${selected?.title ? ` — ${selected.title}` : ''}`}
              </p>
              {isPrimary && <p className="text-xs text-muted-foreground">Primary contact</p>}
              {!isPrimary && selected?.title && (
                <p className="text-xs text-muted-foreground">{selected.title}</p>
              )}
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-2" align="start">
          <div className="space-y-1">
            {allOptions.map((opt) => {
              const isSelected = opt.id === value;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => {
                    onChange(opt.id);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center gap-3 rounded-md p-2.5 text-left text-sm transition-colors ${isSelected ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}`}
                >
                  <User className="h-3.5 w-3.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{opt.name}</p>
                    {opt.title && <p className="text-xs text-muted-foreground">{opt.title}</p>}
                  </div>
                  {isSelected && <Check className="h-3.5 w-3.5 shrink-0" />}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export { ContactPersonCard, SUB_CONTACT_NONE };
export type { ContactPersonCardProps };
